import { DEFAULT_COLLECTION_STEPS } from "./collection";
import { sanitizeCopilotReply } from "./copilot";
import { PLANNER_DEFAULTS } from "./demand-forecast";
import { isValidCest, isValidGtin, isValidNcm } from "./product";
import { SENTINEL_DEFAULTS } from "./sentinel";
import { cellText, isBlankRow, normalizeHeader, parseDecimalCell, type SheetCell } from "./spreadsheet";

/**
 * Agentes de IA (T3.7, 06-ia-agentes §3/§5): nomes, configuração padrão, limites, limpeza do texto do modelo, textos
 * padrão (sem modelo) e a parte determinística do cadastro inteligente. O modelo só redige e classifica; nada aqui
 * depende dele.
 */

export const PURCHASE_PLANNER_AGENT = "purchase_planner";
export const COLLECTOR_AGENT = "collector";
export const ANOMALY_AGENT = "anomaly";
export const PRODUCT_ENRICHER_AGENT = "product_enricher";
export const AI_AGENTS = [
  PURCHASE_PLANNER_AGENT,
  COLLECTOR_AGENT,
  ANOMALY_AGENT,
  PRODUCT_ENRICHER_AGENT,
] as const;
export type AiAgent = (typeof AI_AGENTS)[number];
/** Agentes com execução agendada (cron) e "Rodar agora". */
export const SCHEDULED_AGENTS = [PURCHASE_PLANNER_AGENT, COLLECTOR_AGENT, ANOMALY_AGENT] as const;
export type ScheduledAgent = (typeof SCHEDULED_AGENTS)[number];

export const AI_AGENT_LABELS: Record<AiAgent, string> = {
  purchase_planner: "Planejador de compras",
  collector: "Cobrador",
  anomaly: "Sentinela de anomalias",
  product_enricher: "Cadastro inteligente",
};

export type AiAgentConfig = {
  planner: { enabled: boolean; coverDays: number; leadTimeDays: number; warehouseId: string | null };
  collector: { enabled: boolean; steps: number[] };
  sentinel: {
    enabled: boolean;
    stockoutDays: number;
    channelDropPct4: number;
    channelMinBaseCents: number;
    discountSigma: number;
    discountMinGapPct4: number;
  };
  /** Chamadas ao modelo por empresa por dia (0 = só texto padrão). */
  dailyLlmCalls: number;
};

export const DEFAULT_AI_AGENT_CONFIG: AiAgentConfig = {
  planner: {
    enabled: false,
    coverDays: PLANNER_DEFAULTS.coverDays,
    leadTimeDays: PLANNER_DEFAULTS.leadTimeDays,
    warehouseId: null,
  },
  collector: { enabled: false, steps: [...DEFAULT_COLLECTION_STEPS] },
  sentinel: {
    enabled: false,
    stockoutDays: SENTINEL_DEFAULTS.stockoutDays,
    channelDropPct4: SENTINEL_DEFAULTS.channelDropPct4,
    channelMinBaseCents: SENTINEL_DEFAULTS.channelMinBaseCents,
    discountSigma: SENTINEL_DEFAULTS.discountSigma,
    discountMinGapPct4: SENTINEL_DEFAULTS.discountMinGapPct4,
  },
  dailyLlmCalls: 50,
};

export const AGENT_LIMITS = {
  maxDailyLlmCalls: 1000,
  plannerProducts: 2000,
  plannerSuppliers: 50,
  plannerItemsPerSuggestion: 100,
  collectorTitles: 50,
  sentinelAlerts: 30,
  enricherProducts: 20,
  enricherChars: 20_000,
  enricherRows: 200,
  enricherFileBytes: 1024 * 1024,
  /** Execução "running" mais velha que isto é abandonada (outra pode começar). */
  runStaleMs: 10 * 60_000,
  /** Texto do modelo: mensagem do cobrador e justificativas. */
  collectorTextMax: 600,
  rationaleMax: 400,
  forecastTextMax: 800,
} as const;

/** Confiança mínima para não marcar "conferir" (06 §3: classificador abaixo de 0,8 não decide sozinho). */
export const ENRICHER_MIN_CONFIDENCE = 0.8;

// eslint-disable-next-line no-control-regex -- controles saem antes de qualquer outra limpeza
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f]/g;
const MONEY = /R\$\s*-?\s*[\d.]+(?:,\d{1,2})?/gi;
const TAG = /<[^>]{0,200}>/g;
/** Chave Pix aleatória (UUID). */
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
/** Sequência de dígitos com separadores (telefone, CPF/CNPJ, conta, linha digitável, código). */
const DIGIT_RUN = /\(?\d[\d \t.\-/()]*\d/g;
const LONG_DIGITS = 5;
/** Valor sem "R$": "1.234,56", "99,90", "1500 reais". */
const DECIMAL = /\d{1,3}(?:\.\d{3})*,\d{1,2}(?!\d)/g;
const REAIS = /\d[\d.,]*\s*(?:reais|real)\b/gi;

/**
 * Texto do modelo antes de ir à tela ou ao cliente: sem controles, HTML, links, e-mails, domínios (a limpeza do
 * Copiloto), sem valores (com ou sem R$) e sem sequências de 5+ dígitos ou UUID (chave Pix, telefone, documento,
 * linha digitável: valor e dados de pagamento só vêm do código); espaços normalizados e corte em `max`.
 */
export function cleanAgentText(text: string, max: number): string {
  // Revisão de segurança: nenhum número que pague ou identifique (chave Pix, telefone, documento, conta, linha
  // digitável) nem valor sem "R$" — dados de pagamento e valores vêm só do sistema.
  const noPay = text
    .replace(CONTROL, " ")
    .replace(TAG, " ")
    .replace(MONEY, "")
    .replace(UUID, "")
    .replace(REAIS, "")
    .replace(DECIMAL, "")
    .replace(DIGIT_RUN, (m) => ((m.match(/\d/g)?.length ?? 0) >= LONG_DIGITS ? "" : m));
  const t = sanitizeCopilotReply(noPay)
    .replace(/\[link removido\]/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return t.length > max ? t.slice(0, max) : t;
}

export function plannerFallbackRationale(i: {
  supplierName: string;
  items: number;
  belowReorder: number;
  noHistory: number;
  leadTimeDays: number;
}): string {
  const parts: string[] = [];
  if (i.belowReorder) parts.push(`${i.belowReorder} abaixo do ponto de pedido pela previsão de vendas`);
  if (i.noHistory) parts.push(`${i.noHistory} abaixo do estoque mínimo (sem vendas no período)`);
  return `${i.items} produto(s) do fornecedor ${i.supplierName} precisam de reposição: ${parts.join(" e ")}. Prazo de entrega considerado: ${i.leadTimeDays} dias.`;
}

export type AnomalyKind = "negative_margin" | "discount_outlier" | "stockout_risk" | "channel_drop";
export const ANOMALY_LABELS: Record<AnomalyKind, string> = {
  negative_margin: "Margem negativa",
  discount_outlier: "Desconto fora do padrão",
  stockout_risk: "Risco de ruptura",
  channel_drop: "Queda de vendas no canal",
};

export function sentinelFallbackText(kind: AnomalyKind): string {
  switch (kind) {
    case "negative_margin":
      return "O produto foi vendido hoje por menos do que custa. Confira o preço de venda, os descontos e o custo cadastrado.";
    case "discount_outlier":
      return "O desconto deste pedido está bem acima do que o vendedor costuma dar. Confira se foi autorizado.";
    case "stockout_risk":
      return "No ritmo de vendas das últimas 4 semanas, o estoque acaba em poucos dias. Avalie um pedido de compra.";
    case "channel_drop":
      return "As vendas do canal caíram em relação à semana anterior. Confira anúncios, estoque publicado e a integração do canal.";
  }
}

// ---------- cadastro inteligente ----------

export type ProductDraftInput = {
  name?: unknown;
  sku?: unknown;
  shortDescription?: unknown;
  longDescription?: unknown;
  unit?: unknown;
  gtin?: unknown;
  ncm?: unknown;
  ncmConfidence?: unknown;
  cest?: unknown;
  brand?: unknown;
  attributes?: unknown;
  priceCents?: unknown;
  costCents?: unknown;
};

export type ProductDraft = {
  name: string;
  sku: string | null;
  shortDescription: string | null;
  longDescription: string | null;
  unit: string;
  gtin: string | null;
  ncm: string | null;
  /** Confiança do NCM (0–1) quando veio do modelo; null = informado na planilha. */
  ncmConfidence: number | null;
  cest: string | null;
  brand: string | null;
  attributes: { name: string; value: string }[];
  priceCents: number | null;
  costCents: number | null;
  /** Campos a conferir antes de aceitar (descartados ou de baixa confiança). */
  review: string[];
};

const str = (v: unknown, max: number): string | null => {
  if (typeof v !== "string" && typeof v !== "number") return null;
  const s = String(v).replace(CONTROL, " ").replace(/\s+/g, " ").trim();
  return s ? s.slice(0, max) : null;
};
const digits = (v: unknown) => (str(v, 40) ?? "").replace(/\D/g, "");
const cents = (v: unknown): number | null =>
  typeof v === "number" && Number.isSafeInteger(v) && v >= 0 && v <= 100_000_000_000 ? v : null;
const UNIT = /^[A-Z0-9]{1,6}$/;
const UNIT_ALIASES: Record<string, string> = {
  UNID: "UN",
  UND: "UN",
  UNIDADE: "UN",
  PECA: "PC",
  PEÇA: "PC",
  CAIXA: "CX",
  QUILO: "KG",
  LITRO: "L",
  METRO: "M",
};

/** Validação determinística de um produto proposto (do modelo ou da planilha). */
export function normalizeProductDraft(i: ProductDraftInput): ProductDraft {
  const review: string[] = [];
  const rawGtin = digits(i.gtin);
  const gtin = rawGtin && isValidGtin(rawGtin) ? rawGtin : null;
  if (rawGtin && !gtin) review.push("gtin");
  const rawNcm = digits(i.ncm);
  const ncm = rawNcm && isValidNcm(rawNcm) ? rawNcm : null;
  const conf =
    typeof i.ncmConfidence === "number" && i.ncmConfidence >= 0 && i.ncmConfidence <= 1
      ? i.ncmConfidence
      : null;
  if ((rawNcm && !ncm) || (ncm && conf !== null && conf < ENRICHER_MIN_CONFIDENCE)) review.push("ncm");
  const rawCest = digits(i.cest);
  const cest = rawCest && isValidCest(rawCest) ? rawCest : null;
  if (rawCest && !cest) review.push("cest");
  const u = (str(i.unit, 20) ?? "UN").toUpperCase();
  const unit = UNIT_ALIASES[u] ?? (UNIT.test(u) ? u : "UN");
  const attributes = Array.isArray(i.attributes)
    ? i.attributes
        .flatMap((a) => {
          if (!a || typeof a !== "object") return [];
          const r = a as Record<string, unknown>;
          const name = str(r.name, 40);
          const value = str(r.value, 80);
          return name && value ? [{ name, value }] : [];
        })
        .slice(0, 10)
    : [];
  return {
    name: str(i.name, 120) ?? "",
    sku: str(i.sku, 60),
    shortDescription: str(i.shortDescription, 255),
    longDescription:
      typeof i.longDescription === "string"
        ? i.longDescription.replace(CONTROL, " ").trim().slice(0, 4000) || null
        : null,
    unit,
    gtin,
    ncm,
    ncmConfidence: ncm ? conf : null,
    cest,
    brand: str(i.brand, 60),
    attributes,
    priceCents: cents(i.priceCents),
    costCents: cents(i.costCents),
    review,
  };
}

const SHEET_COLUMNS: Record<string, keyof ProductDraftInput> = {
  nome: "name",
  produto: "name",
  descricao: "name",
  "descricao do produto": "name",
  sku: "sku",
  codigo: "sku",
  "codigo sku": "sku",
  referencia: "sku",
  gtin: "gtin",
  ean: "gtin",
  "gtin ean": "gtin",
  "codigo de barras": "gtin",
  ncm: "ncm",
  cest: "cest",
  unidade: "unit",
  un: "unit",
  marca: "brand",
  preco: "priceCents",
  "preco de venda": "priceCents",
  "preco venda": "priceCents",
  valor: "priceCents",
  custo: "costCents",
  "preco de custo": "costCents",
  "descricao curta": "shortDescription",
  "descricao complementar": "longDescription",
};

/** Planilha do fornecedor (1ª linha = cabeçalho): colunas reconhecidas pelo nome; até 200 linhas. */
export function productDraftsFromSheet(rows: readonly (readonly SheetCell[])[]): {
  drafts: ProductDraft[];
  error: string | null;
} {
  const header = (rows[0] ?? []).map((h) => SHEET_COLUMNS[normalizeHeader(cellText(h))] ?? null);
  if (!header.includes("name"))
    return { drafts: [], error: "A planilha precisa de uma coluna com o nome (Nome, Produto ou Descrição)." };
  const drafts: ProductDraft[] = [];
  for (const row of rows.slice(1)) {
    if (isBlankRow(row)) continue;
    if (drafts.length >= AGENT_LIMITS.enricherRows) break;
    const raw: ProductDraftInput = {};
    header.forEach((k, j) => {
      if (!k || raw[k] !== undefined) return;
      if (k === "priceCents" || k === "costCents") {
        const r = parseDecimalCell(row[j], 2);
        raw[k] = r.ok ? r.value : null;
      } else raw[k] = cellText(row[j]);
    });
    const d = normalizeProductDraft(raw);
    if (d.name) drafts.push(d);
  }
  return { drafts, error: null };
}
